import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { 
  LayoutDashboard, ShoppingCart, UtensilsCrossed, Package, MessageSquare, 
  AlertTriangle, Star, BarChart3, Settings, Users, LogOut, Menu, X, ChevronRight,
  CalendarDays, ChefHat, FileText, UserPlus, HelpCircle, Tag, Megaphone, TrendingUp, Monitor, Brain,
  MapPin, DollarSign, Truck, Salad, Heart, Wallet, ShieldAlert, Flame, ShieldCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/context/AuthContext';
import RoleOnboarding, { useRoleOnboarding } from '@/components/RoleOnboarding';

/**
 * AUTHORITATIVE BOH ROLE LIST (per specification):
 * - SUPER_ADMIN: Full system control
 * - MANAGER: All access except settings
 * - OPERATIONS_MANAGER: Operational control (no finance)
 * - KITCHEN: Production only - prep sheets, quantities (NO money)
 * - CUSTOMER_SUPPORT: Customer interaction only
 * - FINANCE: Full financial visibility (read-only orders)
 */

// Navigation items with role-based visibility
const getNavItems = (userRole) => {
  const allItems = [
    // Dashboard - all roles except driver (drivers go straight to their dashboard)
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true, roles: ['super_admin', 'manager', 'operations_manager', 'kitchen', 'fulfilment', 'customer_support', 'finance', 'admin', 'staff'] },
    
    // Orders - super_admin, manager, operations_manager, finance (read)
    { path: '/admin/orders', icon: ShoppingCart, label: 'Orders', roles: ['super_admin', 'manager', 'operations_manager', 'finance'] },
    
    // Menu Management - super_admin, manager, operations_manager
    { path: '/admin/menu-schedule', icon: CalendarDays, label: 'Weekly Menu', roles: ['super_admin', 'manager', 'operations_manager'] },
    { path: '/admin/auto-scheduler', icon: Brain, label: 'Auto Scheduler', roles: ['super_admin', 'manager', 'operations_manager'] },
    { path: '/admin/menu', icon: UtensilsCrossed, label: 'Menu Items', roles: ['super_admin', 'manager', 'operations_manager'] },
    { path: '/admin/sides', icon: Salad, label: 'Sides', roles: ['super_admin', 'manager', 'operations_manager'] },
    { path: '/admin/specials', icon: Flame, label: 'Specials & Meal of Day', roles: ['super_admin', 'manager', 'operations_manager'] },
    { path: '/admin/menu-ai-suggestions', icon: Brain, label: 'AI Menu Ideas', roles: ['super_admin', 'manager', 'operations_manager'] },
    
    // Kitchen - super_admin, manager, operations_manager, kitchen
    { path: '/admin/kitchen', icon: ChefHat, label: 'Kitchen Prep', roles: ['super_admin', 'manager', 'operations_manager', 'kitchen'] },
    { path: '/admin/kds', icon: Monitor, label: 'Kitchen Display', roles: ['super_admin', 'manager', 'operations_manager', 'kitchen'] },
    
    // Stock - super_admin, manager, operations_manager
    { path: '/admin/stock', icon: Package, label: 'Stock', roles: ['super_admin', 'manager', 'operations_manager'] },
    
    // Customer Support features - super_admin, manager, customer_support
    { path: '/admin/chat', icon: MessageSquare, label: 'Messages', roles: ['super_admin', 'manager', 'customer_support'] },
    { path: '/admin/food-safety', icon: AlertTriangle, label: 'Food Safety', roles: ['super_admin', 'manager', 'customer_support'] },
    { path: '/admin/reviews', icon: Star, label: 'Reviews', roles: ['super_admin', 'manager', 'customer_support'] },
    
    // Reports & Analytics - super_admin, manager, finance
    { path: '/admin/reports', icon: FileText, label: 'Reports', roles: ['super_admin', 'manager', 'finance'] },
    { path: '/admin/loyalty', icon: Heart, label: 'Loyalty', roles: ['super_admin', 'manager', 'finance'] },
    { path: '/admin/wallets', icon: Wallet, label: 'Wallet Deposits', roles: ['super_admin', 'manager', 'finance', 'admin'] },
    { path: '/admin/disputes', icon: ShieldAlert, label: 'Disputes & Refunds', roles: ['super_admin', 'manager', 'finance', 'customer_support'] },
    { path: '/admin/analytics', icon: TrendingUp, label: 'Analytics', roles: ['super_admin', 'manager', 'finance'] },
    { path: '/admin/forecasting', icon: Brain, label: 'Forecasting (AI)', roles: ['super_admin', 'manager'] },
    
    // Marketing - super_admin, manager
    { path: '/admin/promo-codes', icon: Tag, label: 'Promo Codes', roles: ['super_admin', 'manager'] },
    { path: '/admin/announcements', icon: Megaphone, label: 'Announcements', roles: ['super_admin', 'manager'] },
    
    // Staff Management - super_admin, manager, operations_manager
    { path: '/admin/staff-scheduling', icon: CalendarDays, label: 'Staff Scheduling', roles: ['super_admin', 'manager', 'operations_manager'] },
    
    // Delivery & Fees - super_admin, manager
    { path: '/admin/delivery-addresses', icon: MapPin, label: 'Delivery Addresses', roles: ['super_admin', 'manager', 'operations_manager'] },
    { path: '/admin/driver', icon: Truck, label: 'Driver Dashboard', roles: ['super_admin', 'manager', 'driver'] },
    { path: '/admin/platform-fees', icon: DollarSign, label: 'Platform Fees', roles: ['super_admin', 'manager'] },
    
    // User management - super_admin, manager
    { path: '/admin/users', icon: Users, label: 'Users', roles: ['super_admin', 'manager'] },
    { path: '/admin/invitations', icon: UserPlus, label: 'Invitations', roles: ['super_admin', 'manager'] },
    
    // Settings - super_admin ONLY (Manager cannot access)
    { path: '/admin/permissions', icon: ShieldCheck, label: 'Staff Permissions', roles: ['super_admin'] },
    { path: '/admin/settings', icon: Settings, label: 'System Settings', roles: ['super_admin'] },
  ];

  // Filter based on role - strict enforcement
  return allItems.filter(item => {
    if (!item.roles) return true; // No role restriction = visible to all
    if (item.roles.includes(userRole)) return true;
    // Legacy 'admin' role acts as a full manager across the BOH
    if (userRole === 'admin' && item.roles.includes('manager')) return true;
    return false;
  });
};

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Role onboarding hook
  const { showOnboarding, closeOnboarding, resetOnboarding } = useRoleOnboarding(user?.role);

  // Redirect driver to their dashboard if they land on /admin
  useEffect(() => {
    if (user?.role === 'driver' && location.pathname === '/admin') {
      navigate('/admin/driver', { replace: true });
    }
  }, [user?.role, location.pathname, navigate]);

  const navItems = getNavItems(user?.role);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  // Format role for display
  const formatRole = (role) => {
    if (!role) return '';
    return role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="min-h-screen flex bg-slate-100" data-testid="admin-layout">
      {/* Sidebar - Clean Dark Theme */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 
        transform transition-transform duration-200 ease-in-out
        lg:relative lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-5 border-b border-slate-700">
            <Link to="/admin" className="flex items-center gap-3">
              <img 
                src="https://gc3h.co.za/wp-content/uploads/2022/02/gallagher-convention-centre-catering.png"
                alt="GC3H"
                className="h-10 w-auto"
              />
            </Link>
            <p className="text-xs text-slate-400 mt-2 font-medium">Back-of-House Portal</p>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {navItems.map(item => {
                const Icon = item.icon;
                const active = isActive(item.path, item.exact);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-150
                      ${active 
                        ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30' 
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                      }
                    `}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Icon className={`h-5 w-5 ${active ? 'text-white' : 'text-slate-400'}`} />
                    <span>{item.label}</span>
                    {active && <ChevronRight className="h-4 w-4 ml-auto" />}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User Info */}
          <div className="p-4 border-t border-slate-700 bg-slate-800/50">
            <div className="flex items-center gap-3 mb-4">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="h-10 w-10 rounded-full ring-2 ring-amber-500" />
              ) : (
                <div className="h-10 w-10 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold text-lg">
                  {user?.name?.[0]?.toUpperCase() || 'A'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{user?.name || 'Admin'}</p>
                <p className="text-xs text-amber-400 truncate font-medium">{formatRole(user?.role)}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Link to="/" className="flex-1">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full text-slate-300 border-slate-600 hover:bg-slate-700 hover:text-white hover:border-slate-500"
                >
                  View Site
                </Button>
              </Link>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-slate-400 hover:text-white hover:bg-slate-700"
                data-testid="admin-logout-btn"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile Header */}
        <header className="lg:hidden bg-slate-900 p-4 flex items-center justify-between sticky top-0 z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-white p-2 hover:bg-slate-800 rounded-lg transition-colors"
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-6 w-6" />
          </button>
          <img 
            src="https://gc3h.co.za/wp-content/uploads/2022/02/gallagher-convention-centre-catering.png"
            alt="GC3H"
            className="h-8 w-auto"
          />
          <div className="w-10" />
        </header>

        {/* Page Content */}
        <main className="flex-1 bg-background p-6 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Role Onboarding Modal */}
      <RoleOnboarding 
        isOpen={showOnboarding} 
        onClose={closeOnboarding} 
        userRole={user?.role} 
      />
    </div>
  );
}
